import React, { useState, useEffect, useCallback } from 'react';

type DeviceAuthentication = {
    initAuthentication?: boolean;
    authenticationDescription?: string;
    enableAuthentication?: boolean;
    onAuthenticationTimeOut?: () => void;
};

export function useTimer() {
    const [time, setTime] = useState<number>(0);
    const [isTimerStart, setIsTimerStart] = useState<boolean>(false);
    const stop = useCallback(() => {
        setIsTimerStart(false);
    }, []);
    const start = useCallback(() => {
        setIsTimerStart(true);
    }, []);
    useEffect(() => {
        let interval: any;
        if (isTimerStart) {
            interval = setInterval(() => {
                setTime((_time) => _time + 1);
            }, 1000);
        }
        return () => {
            clearInterval(interval);
        };
    }, [isTimerStart]);
    return {
        time,
        start,
        stop,
        isTimerStart,
        setTime,
    };
}

export const isFingerPrintAvailable = (
    onSuccess: Function = () => { },
    onFail: Function = () => { },
) => {
    const _window: any = window;
    const { Fingerprint }: any = _window;
    if (Fingerprint) {
        Fingerprint.isAvailable(onSuccess, onFail, {});
    }
};

export function useDeviceAuthentication({
    initAuthentication = false,
    authenticationDescription = 'Touch Id required to use MfCentral',
    enableAuthentication = false,
    onAuthenticationTimeOut = () => { },
}: DeviceAuthentication) {
    const _window: any = window;
    const [authenticated, setIsAuthenticated] = useState<boolean>(false);
    const [failedAttempts, setFailedAttempts] = useState<number>(0);
    const { time, stop, start, setTime, isTimerStart } = useTimer();

    useEffect(() => {
        function onResume() {
            stop();
        }
        function onPause() {
            start();
        }
        if (_window.cordova) {
            document.addEventListener('resume', onResume, false);
            document.addEventListener('pause', onPause, false);
        }
        return () => {
            document.removeEventListener('resume', onResume);
            document.removeEventListener('pause', onPause);
        };
    }, []);

    useEffect(() => {
        if (time > 120 && !isTimerStart) {
            onAuthenticationTimeOut();
            setTime(0);
        }
    }, [time, isTimerStart]);

    const askAuthentication = () => {
        // ignoring when user not enabled device authentication
        if (!enableAuthentication) {
            return;
        }
        const onSuccess = () => {
            const { Fingerprint, device }: any = _window;

            let androidAuthOptions: any = {
                disableBackup: true,
            };
            let iosAuthOptions: any = {
                allowBackup: false,
            };
            let authenticationOptions: any = {
                description: authenticationDescription,
                ...(device?.platform === 'Android' ? androidAuthOptions : iosAuthOptions),
            };
            if (Fingerprint) {
                Fingerprint.show(
                    authenticationOptions,
                    () => {
                        setIsAuthenticated(true);
                    },
                    () => {
                        setFailedAttempts((count) => count + 1);
                    },
                );
            }
        };
        isFingerPrintAvailable(onSuccess, (error: any) => {
            alert(error.message);
        });
    };

    useEffect(() => {
        if (_window.cordova && enableAuthentication && !initAuthentication) {
            askAuthentication();
        }
    }, []);
    return {
        authenticated,
        askAuthentication,
        failedAttempts,
    };
}
